import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// =====================================================
// INTERFACES
// =====================================================

interface Appointment {
  id: number;
  professional_id: number;
  client_name?: string;
  client_display_name: string;
  client_email?: string;
  client_phone?: string;
  service_name: string;
  service_category?: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  price: number;
  deposit_required?: boolean;
  deposit_amount?: number;
  deposit_paid?: boolean;
  notes?: string;
  professional_notes?: string;
  is_first_time?: boolean;
  urgency_level?: 'low' | 'normal' | 'high';
  created_at?: string;
  updated_at?: string;
  location_id?: number;
  location_name?: string;
  location_city?: string;
  is_override?: boolean;
}

interface WorkSchedule {
  day_of_week: number;
  start_time: string;
  end_time: string;
  break_start?: string;
  break_end?: string;
  is_working: boolean;
}

interface BlockedHoursInfo {
  blockedFrom: string; // "19:00"
  blockedUntil?: string; // "21:00" — si falta, bloqueado hasta el fin del día
  reason?: string;
}

// Día especial cargado para la fecha (schedule_exceptions custom_hours): el
// día se atiende en este horario aunque no figure en el horario semanal.
interface SpecialDayInfo {
  id?: number;
  start: string; // "09:00"
  end: string; // "12:30"
  breakStart?: string | null;
  breakEnd?: string | null;
  reason?: string | null;
}

interface OpenSpecialDayData {
  start: string;
  end: string;
  breakStart?: string;
  breakEnd?: string;
  reason?: string;
  locationId?: number | null;
}

// Día cerrado entero desde Horarios → Bloqueos (schedule_exceptions con
// is_working = false, salvo blocked_hours, que es el bloqueo de la agenda).
interface DiaCerradoInfo {
  tipo: string; // holiday | vacation | personal | sick | other
  motivo?: string | null;
  sedeId?: number | null;
}

const TIPOS_DE_CIERRE: Record<string, string> = {
  holiday: 'feriado', vacation: 'vacaciones', personal: 'motivos personales', sick: 'enfermedad'
};

// La excepción que cierra el día entero, si hay (para armar DiaCerradoInfo)
const cierreDelDia = (excepciones: any[], fecha: string): DiaCerradoInfo | null => {
  const ex = excepciones.find((e: any) =>
    String(e.exception_date || '').substring(0, 10) === fecha
    && !e.is_working && e.exception_type !== 'blocked_hours');
  return ex ? { tipo: ex.exception_type, motivo: ex.reason || null, sedeId: ex.location_id ?? null } : null;
};

interface ServiceOption {
  id: number;
  name: string;
  duration: number;
  price: number;
}

interface QuickAddData {
  date: string;
  time: string;
  clientName: string;
  clientPhone?: string;
  serviceName: string;
  serviceId?: number;
  duration: number;
  notes?: string;
  isOverride?: boolean;
}

interface DayTimelineViewProps {
  selectedDate: Date;
  appointments: any[]; // Acepta cualquier tipo de appointment
  workSchedule: WorkSchedule | null;
  onAppointmentClick: (appointment: any) => void;
  onWhatsAppClick: (appointment: any) => void;
  professionalName?: string;
  // Bloqueo de horarios
  blockedHours?: BlockedHoursInfo | null;
  onBlockHours?: (fromTime: string, untilTime?: string, reason?: string) => Promise<boolean> | void;
  onUnblockHours?: () => Promise<boolean> | void;
  // Día especial: abrir un día que no se atiende (y quitarlo)
  specialDay?: SpecialDayInfo | null;
  onOpenSpecialDay?: (data: OpenSpecialDayData) => Promise<boolean> | void;
  onRemoveSpecialDay?: () => Promise<boolean> | void;
  sedes?: { id: number; name: string }[];
  defaultSedeId?: number | null;
  // Día cerrado desde Horarios → Bloqueos (vacaciones, feriado…)
  diaCerrado?: DiaCerradoInfo | null;
  // Sobreturnos
  onAddAppointment?: (date: string, time: string) => void;
  maxOverridesPerSlot?: number;
  // Agenda libre: QuickAdd
  services?: ServiceOption[];
  onQuickAdd?: (data: QuickAddData) => Promise<boolean>;
}

// =====================================================
// UTILIDADES
// =====================================================

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const getStatusColor = (status: string): { bg: string; border: string; text: string } => {
  switch (status) {
    case 'confirmed':
      return { bg: 'bg-emerald-50', border: 'border-l-emerald-500', text: 'text-emerald-700' };
    case 'pending':
      return { bg: 'bg-amber-50', border: 'border-l-amber-500', text: 'text-amber-700' };
    case 'completed':
      return { bg: 'bg-sky-50', border: 'border-l-sky-500', text: 'text-sky-700' };
    case 'cancelled':
      return { bg: 'bg-gray-50', border: 'border-l-gray-300', text: 'text-gray-400' };
    case 'no_show':
      return { bg: 'bg-gray-100', border: 'border-l-gray-400', text: 'text-gray-600' };
    default:
      return { bg: 'bg-gray-50', border: 'border-l-gray-400', text: 'text-gray-700' };
  }
};

const getStatusBadge = (status: string): { label: string; class: string } => {
  switch (status) {
    case 'confirmed':
      return { label: 'Confirmado', class: 'bg-emerald-100 text-emerald-700' };
    case 'pending':
      return { label: 'Pendiente', class: 'bg-amber-100 text-amber-700' };
    case 'completed':
      return { label: 'Completado', class: 'bg-sky-100 text-sky-700' };
    case 'cancelled':
      return { label: 'Cancelado', class: 'bg-gray-200 text-gray-500' };
    case 'no_show':
      return { label: 'No asistió', class: 'bg-gray-200 text-gray-600' };
    default:
      return { label: status, class: 'bg-gray-100 text-gray-600' };
  }
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const DayTimelineView: React.FC<DayTimelineViewProps> = ({
  selectedDate,
  appointments,
  workSchedule,
  onAppointmentClick,
  onWhatsAppClick,
  professionalName = 'Profesional',
  blockedHours,
  onBlockHours,
  onUnblockHours,
  onAddAppointment,
  maxOverridesPerSlot = 0,
  services = [],
  onQuickAdd,
  specialDay = null,
  onOpenSpecialDay,
  onRemoveSpecialDay,
  sedes = [],
  defaultSedeId = null,
  diaCerrado = null
}) => {
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockFromTime, setBlockFromTime] = useState('');
  const [blockUntilTime, setBlockUntilTime] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [isBlocking, setIsBlocking] = useState(false);
  // Abrir el día (día especial)
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [openFrom, setOpenFrom] = useState('09:00');
  const [openUntil, setOpenUntil] = useState('13:00');
  const [openConCorte, setOpenConCorte] = useState(false);
  const [openBreakFrom, setOpenBreakFrom] = useState('');
  const [openBreakUntil, setOpenBreakUntil] = useState('');
  const [openReason, setOpenReason] = useState('');
  const [openSedeId, setOpenSedeId] = useState<number | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const [isRemovingSpecial, setIsRemovingSpecial] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  // QuickAdd state
  const [quickAdd, setQuickAdd] = useState<{ time: string; top: number } | null>(null);
  const [qaName, setQaName] = useState('');
  const [qaPhone, setQaPhone] = useState('');
  const [qaService, setQaService] = useState('');
  const [qaServiceId, setQaServiceId] = useState<number | null>(null);
  const [qaDuration, setQaDuration] = useState(30);
  const [qaNotes, setQaNotes] = useState('');
  const [qaTime, setQaTime] = useState('');
  const [qaSaving, setQaSaving] = useState(false);
  const [qaIndicaciones, setQaIndicaciones] = useState('');
  const [qaSendWhatsApp, setQaSendWhatsApp] = useState(true);
  // Expanded slots for overflow sobreturnos
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());
  // Configuración del timeline
  const SLOT_HEIGHT = 60; // px por cada 30 minutos
  const START_HOUR = 7; // 07:00
  const END_HOUR = 22; // 22:00
  
  // Generar slots de tiempo (cada 30 min)
  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < END_HOUR) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    return slots;
  }, []);

  // (overlapLayout, getAppointmentStyle y getOverlapInfo definidos después de dayAppointments)

  // Horario con el que se dibuja el día: el del día especial si hay uno; si no,
  // el semanal. Sin esto, un día abierto como especial (que en el horario
  // semanal no se atiende) se veía entero "fuera de horario" y no se podía
  // cargar un turno tocando un horario.
  const horario: WorkSchedule | null = useMemo(() => (
    specialDay
      ? {
          day_of_week: selectedDate.getDay(),
          start_time: specialDay.start,
          end_time: specialDay.end,
          break_start: specialDay.breakStart || undefined,
          break_end: specialDay.breakEnd || undefined,
          is_working: true
        }
      : workSchedule
  ), [specialDay, workSchedule, selectedDate]);
  const diaNoLaboral = !horario || !horario.is_working;

  // Verificar si un slot está en horario de descanso
  const isBreakTime = (slotTime: string): boolean => {
    if (!horario?.break_start || !horario?.break_end) return false;
    
    const slotMinutes = timeToMinutes(slotTime);
    const breakStart = timeToMinutes(horario.break_start.substring(0, 5));
    const breakEnd = timeToMinutes(horario.break_end.substring(0, 5));
    
    return slotMinutes >= breakStart && slotMinutes < breakEnd;
  };

  // Verificar si un slot está fuera del horario laboral
  const isOutsideWorkHours = (slotTime: string): boolean => {
    if (!horario?.start_time || !horario?.end_time) return false;
    if (!horario.is_working) return true;

    const slotMinutes = timeToMinutes(slotTime);
    const workStart = timeToMinutes(horario.start_time.substring(0, 5));
    const workEnd = timeToMinutes(horario.end_time.substring(0, 5));

    return slotMinutes < workStart || slotMinutes >= workEnd;
  };

  // Verificar si un slot está bloqueado manualmente
  const isBlockedSlot = (slotTime: string): boolean => {
    if (!blockedHours) return false;
    const slotMinutes = timeToMinutes(slotTime);
    const blockedFromMinutes = timeToMinutes(blockedHours.blockedFrom);
    if (blockedHours.blockedUntil) {
      // Bloqueo por rango: solo la franja desde-hasta
      return slotMinutes >= blockedFromMinutes && slotMinutes < timeToMinutes(blockedHours.blockedUntil);
    }
    return slotMinutes >= blockedFromMinutes;
  };

  // Verificar si un slot es tiempo pasado (solo aplica al día de hoy)
  const isPastTime = (slotTime: string): boolean => {
    const today = new Date();
    const isToday = selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();
    if (!isToday) return false;
    const slotMinutes = timeToMinutes(slotTime);
    const nowMinutes = today.getHours() * 60 + today.getMinutes();
    return slotMinutes < nowMinutes;
  };

  // Auto-scroll al horario actual al montar o cambiar de día
  useEffect(() => {
    if (!timelineRef.current) return;
    const today = new Date();
    const isToday = selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();

    if (isToday) {
      const nowMinutes = today.getHours() * 60 + today.getMinutes();
      const baseMinutes = START_HOUR * 60;
      // Scroll a 1 hora antes de la hora actual para dar contexto
      const scrollToMinutes = Math.max(nowMinutes - 60, baseMinutes);
      const scrollPosition = ((scrollToMinutes - baseMinutes) / 30) * SLOT_HEIGHT;
      timelineRef.current.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    } else {
      // Para otros días, scroll al inicio del horario laboral
      if (horario?.is_working && horario?.start_time) {
        const workStartMinutes = timeToMinutes(horario.start_time.substring(0, 5));
        const baseMinutes = START_HOUR * 60;
        const scrollPosition = ((workStartMinutes - baseMinutes) / 30) * SLOT_HEIGHT;
        timelineRef.current.scrollTo({ top: Math.max(scrollPosition - SLOT_HEIGHT, 0), behavior: 'smooth' });
      }
    }
  }, [selectedDate, horario]);

  // Verificar si un slot está libre (sin turno)
  const isSlotFree = (slotTime: string): boolean => {
    const slotMinutes = timeToMinutes(slotTime);
    return !dayAppointments.some(apt => {
      if (apt.status === 'cancelled') return false;
      const aptStart = timeToMinutes(apt.appointment_time.substring(0, 5));
      const aptEnd = aptStart + (apt.duration || 30);
      return slotMinutes >= aptStart && slotMinutes < aptEnd;
    });
  };

  // Contar turnos activos en un horario específico
  const getAppointmentCountAtTime = (slotTime: string): number => {
    const slotMinutes = timeToMinutes(slotTime);
    return dayAppointments.filter(apt => {
      if (apt.status === 'cancelled') return false;
      const aptStart = timeToMinutes(apt.appointment_time.substring(0, 5));
      return slotMinutes === aptStart;
    }).length;
  };

  // Verificar si un slot puede recibir sobreturno
  const canAddOverride = (slotTime: string): boolean => {
    if (maxOverridesPerSlot <= 0) return false;
    if (isSlotFree(slotTime)) return false; // slot libre, no es sobreturno
    const count = getAppointmentCountAtTime(slotTime);
    return count <= maxOverridesPerSlot; // original + sobreturnos <= max
  };

  // Click en slot (vacío o sobreturno)
  const handleSlotClick = (slotTime: string, event?: React.MouseEvent) => {
    if (isPastTime(slotTime)) return;
    if (isOutsideWorkHours(slotTime) || isBreakTime(slotTime)) return;
    if (isBlockedSlot(slotTime)) return;
    if (!isSlotFree(slotTime) && !canAddOverride(slotTime)) return;

    // Si hay QuickAdd disponible, usarlo en lugar de navegar
    if (onQuickAdd) {
      const slotMinutes = timeToMinutes(slotTime);
      const baseMinutes = START_HOUR * 60;
      const topPx = ((slotMinutes - baseMinutes) / 30) * SLOT_HEIGHT;
      setQuickAdd({ time: slotTime, top: topPx });
      setQaTime(slotTime); setQaName(''); setQaPhone(''); setQaService(''); setQaServiceId(null);
      setQaDuration(30); setQaNotes(''); setQaSaving(false); setQaIndicaciones(''); setQaSendWhatsApp(true);
      return;
    }

    if (onAddAppointment) {
      const dateStr = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}`;
      onAddAppointment(dateStr, slotTime);
    }
  };

  // QuickAdd: seleccionar servicio de lista
  const handleQaServiceSelect = (serviceId: number) => {
    const svc = services.find(s => s.id === serviceId);
    if (svc) {
      setQaServiceId(svc.id);
      setQaService(svc.name);
      setQaDuration(svc.duration);
    }
  };

  // QuickAdd: submit
  const handleQuickAddSubmit = async () => {
    if (!qaName.trim() || !qaService.trim() || !qaTime || !quickAdd) return;
    setQaSaving(true);
    const dateStr = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}`;
    // Combinar notas + indicaciones para el paciente
    const combinedNotes = [
      qaNotes.trim(),
      qaSendWhatsApp && qaIndicaciones.trim() ? qaIndicaciones.trim() : ''
    ].filter(Boolean).join(' | ') || undefined;

    // Detectar si es sobreturno: hay turno activo a esa hora
    const isOverride = !isSlotFree(qaTime);

    const success = await onQuickAdd!({
      date: dateStr,
      time: qaTime,
      clientName: qaName.trim(),
      clientPhone: qaPhone.trim() || undefined,
      serviceName: qaService.trim(),
      serviceId: qaServiceId || undefined,
      duration: qaDuration,
      notes: combinedNotes,
      isOverride
    });
    setQaSaving(false);
    if (success) setQuickAdd(null);
  };

  // Filtrar turnos del día seleccionado
  const dayAppointments = useMemo(() => {
    const dateStr = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}`;
    return appointments.filter(apt => {
      const aptDate = apt.appointment_date.split('T')[0];
      return aptDate === dateStr;
    }).sort((a, b) => a.appointment_time.localeCompare(b.appointment_time));
  }, [appointments, selectedDate]);

  // ✅ Algoritmo de column packing para turnos solapados (estilo Google Calendar / Caluapp)
  // Con límite de columnas visibles: si hay más de MAX_VISIBLE_COLS, los extra se ocultan
  const MAX_VISIBLE_COLS = 3;

  const overlapLayout = useMemo(() => {
    if (dayAppointments.length === 0) return new Map<number, { totalColumns: number; columnIndex: number; hidden: boolean }>();

    // 1. Convertir cada turno a { id, start, end } en minutos
    const intervals = dayAppointments.map(apt => {
      const start = timeToMinutes(apt.appointment_time.substring(0, 5));
      const end = start + (apt.duration || 30);
      return { id: apt.id, start, end };
    });

    // 2. Ordenar por inicio, desempatar por duración más larga primero
    intervals.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));

    // 3. Agrupar en clusters de turnos que se solapan (transitivamente)
    const clusters: typeof intervals[] = [];
    let currentCluster: typeof intervals = [];
    let clusterEnd = -1;

    for (const interval of intervals) {
      if (currentCluster.length === 0 || interval.start < clusterEnd) {
        currentCluster.push(interval);
        clusterEnd = Math.max(clusterEnd, interval.end);
      } else {
        clusters.push(currentCluster);
        currentCluster = [interval];
        clusterEnd = interval.end;
      }
    }
    if (currentCluster.length > 0) clusters.push(currentCluster);

    // 4. Asignar columnas greedy para cada cluster
    const layoutMap = new Map<number, { totalColumns: number; columnIndex: number; hidden: boolean }>();

    for (const cluster of clusters) {
      const columns: number[] = []; // columns[i] = minuto en que la columna i queda libre

      for (const interval of cluster) {
        let placed = false;
        for (let col = 0; col < columns.length; col++) {
          if (columns[col] <= interval.start) {
            columns[col] = interval.end;
            layoutMap.set(interval.id, { totalColumns: 0, columnIndex: col, hidden: false });
            placed = true;
            break;
          }
        }
        if (!placed) {
          layoutMap.set(interval.id, { totalColumns: 0, columnIndex: columns.length, hidden: false });
          columns.push(interval.end);
        }
      }

      const totalCols = columns.length;
      const cappedCols = Math.min(totalCols, MAX_VISIBLE_COLS);
      for (const interval of cluster) {
        const info = layoutMap.get(interval.id)!;
        info.totalColumns = totalCols;
        // Hide appointments in columns beyond MAX_VISIBLE_COLS (unless slot is expanded)
        info.hidden = info.columnIndex >= MAX_VISIBLE_COLS;
      }
    }

    return layoutMap;
  }, [dayAppointments]);

  // Group appointments by their time slot for the overflow indicator
  const slotOverflowInfo = useMemo(() => {
    const map = new Map<string, { total: number; hidden: number; hiddenAppts: Appointment[] }>();
    for (const apt of dayAppointments) {
      const time = apt.appointment_time.substring(0, 5);
      const layout = overlapLayout.get(apt.id);
      if (!layout || layout.totalColumns <= MAX_VISIBLE_COLS) continue;

      if (!map.has(time)) {
        map.set(time, { total: 0, hidden: 0, hiddenAppts: [] });
      }
      const info = map.get(time)!;
      info.total++;
      if (layout.hidden) {
        info.hidden++;
        info.hiddenAppts.push(apt);
      }
    }
    return map;
  }, [dayAppointments, overlapLayout]);

  // Calcular posición y altura de un turno
  const getAppointmentStyle = (appointment: Appointment) => {
    const startMinutes = timeToMinutes(appointment.appointment_time.substring(0, 5));
    const baseMinutes = START_HOUR * 60;
    const topPosition = ((startMinutes - baseMinutes) / 30) * SLOT_HEIGHT;
    const height = (appointment.duration / 30) * SLOT_HEIGHT;
    const layout = overlapLayout.get(appointment.id) || { totalColumns: 1, columnIndex: 0, hidden: false };

    if (layout.totalColumns > 1) {
      // Cap visible columns: use min(totalColumns, MAX_VISIBLE_COLS) for width calc
      const visibleCols = Math.min(layout.totalColumns, MAX_VISIBLE_COLS);
      // If overflow exists, reserve space for the "+N" indicator (last ~15%)
      const hasOverflow = layout.totalColumns > MAX_VISIBLE_COLS;
      const availableWidth = hasOverflow ? 85 : 100; // reserve 15% for overflow badge
      const widthPercent = availableWidth / visibleCols;
      return {
        top: `${topPosition}px`,
        height: `${Math.max(height - 4, SLOT_HEIGHT - 4)}px`,
        left: `calc(${layout.columnIndex * widthPercent}% + 4px)`,
        width: `calc(${widthPercent}% - 8px)`,
        right: 'auto',
        position: 'absolute' as const,
      };
    }

    return {
      top: `${topPosition}px`,
      height: `${Math.max(height - 4, SLOT_HEIGHT - 4)}px`,
    };
  };

  // Helper para obtener info de overlap (usado en el render)
  const getOverlapInfo = (appointment: Appointment) => {
    return overlapLayout.get(appointment.id) || { totalColumns: 1, columnIndex: 0, hidden: false };
  };

  // Formatear fecha para mostrar
  const formattedDate = selectedDate.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="day-timeline-container bg-white rounded-xl border border-gray-300 overflow-hidden shadow-sm">
      {/* Header con nombre del profesional y fecha */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
              {professionalName.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{professionalName}</h3>
              <p className="text-sm text-gray-600 capitalize">{formattedDate}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500 font-medium">
              {dayAppointments.length} turno{dayAppointments.length !== 1 ? 's' : ''}
            </span>
            {/* Abrir el día: en un día que no se atiende, un día especial con
                su propio horario (el mismo de Horarios > Días especiales).
                Para las secretarias (oct-2026): podían bloquear pero no abrir;
                el día especial solo se cargaba desde la configuración de la
                profesional. Arranca con el horario de siempre de ese día. */}
            {onOpenSpecialDay && !specialDay && diaNoLaboral && (
              <button
                onClick={() => {
                  setOpenFrom(workSchedule?.start_time?.substring(0, 5) || '09:00');
                  setOpenUntil(workSchedule?.end_time?.substring(0, 5) || '13:00');
                  const conCorte = !!(workSchedule?.break_start && workSchedule?.break_end);
                  setOpenConCorte(conCorte);
                  setOpenBreakFrom(conCorte ? workSchedule!.break_start!.substring(0, 5) : '');
                  setOpenBreakUntil(conCorte ? workSchedule!.break_end!.substring(0, 5) : '');
                  setOpenReason('');
                  setOpenSedeId(defaultSedeId ?? (sedes.length === 1 ? sedes[0].id : null));
                  setShowOpenModal(true);
                }}
                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors"
                title="Abrir este día con un horario especial"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">Abrir este día</span>
                <span className="sm:hidden">Abrir día</span>
              </button>
            )}
            {/* Botón bloquear/desbloquear horarios */}
            {onBlockHours && !blockedHours && (
              <button
                onClick={() => {
                  // Calcular hora sugerida: después del último turno del día, o ahora
                  const lastApt = dayAppointments[dayAppointments.length - 1];
                  let suggestedTime = '';
                  if (lastApt) {
                    const lastStart = timeToMinutes(lastApt.appointment_time.substring(0, 5));
                    const lastEnd = lastStart + (lastApt.duration || 30);
                    suggestedTime = minutesToTime(lastEnd);
                  } else {
                    const now = new Date();
                    suggestedTime = `${now.getHours().toString().padStart(2, '0')}:${(Math.ceil(now.getMinutes() / 30) * 30 % 60).toString().padStart(2, '0')}`;
                  }
                  setBlockFromTime(suggestedTime);
                  setBlockReason('');
                  setShowBlockModal(true);
                }}
                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors"
                title="Bloquear horarios restantes"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <span className="hidden sm:inline">Bloquear horarios del día</span>
                <span className="sm:hidden">Bloquear</span>
              </button>
            )}
            {onUnblockHours && blockedHours && (
              <button
                onClick={onUnblockHours}
                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors"
                title="Desbloquear horarios"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                <span>Desbloquear</span>
              </button>
            )}
          </div>
        </div>
        {/* Día cerrado desde Horarios → Bloqueos. Antes no se veía acá: la
            secretaria tenía el día como uno común y no sabía por qué no
            entraban reservas (oct-2026). */}
        {diaCerrado && (() => {
          const tipo = TIPOS_DE_CIERRE[diaCerrado.tipo] || '';
          const nota = (diaCerrado.motivo || '').trim();
          const sede = sedes.length > 1 && diaCerrado.sedeId ? sedes.find(s => s.id === diaCerrado.sedeId)?.name : null;
          return (
            <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-left">
              <svg className="w-4 h-4 mt-0.5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-800">
                  Día cerrado{tipo ? ` por ${tipo}` : ''}{sede ? ` en ${sede}` : ''}
                </p>
                {nota && nota.toLowerCase() !== tipo && (
                  <p className="text-xs text-red-700 mt-0.5 truncate">{nota}</p>
                )}
                <p className="text-[11px] text-red-600/80 mt-0.5">
                  Los pacientes no pueden reservar. Se carga en Horarios → Bloqueos.
                </p>
              </div>
            </div>
          );
        })()}
        {/* Día especial cargado para esta fecha */}
        {specialDay && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-left">
            <svg className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-emerald-800">
                Día especial: de {specialDay.start.substring(0, 5)} a {specialDay.end.substring(0, 5)} hs
                {specialDay.breakStart && specialDay.breakEnd
                  ? ` (corte de ${specialDay.breakStart.substring(0, 5)} a ${specialDay.breakEnd.substring(0, 5)})`
                  : ''}
              </p>
              {specialDay.reason && (
                <p className="text-xs text-emerald-700 mt-0.5 truncate">{specialDay.reason}</p>
              )}
              <p className="text-[11px] text-emerald-700/80 mt-0.5">
                Los pacientes pueden reservar en este horario.
              </p>
            </div>
            {onRemoveSpecialDay && (
              <button
                onClick={async () => {
                  const activos = dayAppointments.filter((a: any) => a.status !== 'cancelled').length;
                  const pregunta = activos > 0
                    ? `¿Quitar el día especial? Los ${activos} turnos ya dados se mantienen, pero no se van a poder reservar más.`
                    : '¿Quitar el día especial? Ese día vuelve a quedar sin atención.';
                  if (!window.confirm(pregunta)) return;
                  setIsRemovingSpecial(true);
                  try {
                    await onRemoveSpecialDay();
                  } finally {
                    setIsRemovingSpecial(false);
                  }
                }}
                disabled={isRemovingSpecial}
                className="text-xs font-medium text-emerald-700 hover:text-emerald-900 hover:underline flex-shrink-0 disabled:opacity-50"
              >
                {isRemovingSpecial ? 'Quitando…' : 'Quitar'}
              </button>
            )}
          </div>
        )}
        {/* Tag de horarios bloqueados — visible cuando hay un bloqueo activo */}
        {blockedHours && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
            <svg className="w-4 h-4 mt-0.5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-red-800">
                {blockedHours.blockedUntil
                  ? `Turnos bloqueados de ${blockedHours.blockedFrom} a ${blockedHours.blockedUntil} hs`
                  : `Turnos bloqueados desde las ${blockedHours.blockedFrom} hs`}
              </p>
              {blockedHours.reason && (
                <p className="text-xs text-red-700 mt-0.5 truncate">
                  Motivo: {blockedHours.reason}
                </p>
              )}
              <p className="text-[11px] text-red-600/80 mt-0.5">
                Los pacientes no pueden agendar a partir de este horario.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div ref={timelineRef} className="relative overflow-y-auto max-h-[calc(100vh-280px)] scrollbar-thin scroll-smooth">
        <div className="flex">
          {/* Columna de horas */}
          <div className="flex-shrink-0 w-16 bg-gray-100/80 border-r border-gray-300">
            {timeSlots.map((time, index) => (
              <div
                key={time}
                className="relative"
                style={{ height: `${SLOT_HEIGHT}px` }}
              >
                {time.endsWith(':00') && (
                  <span className="absolute -top-2 right-2 text-xs font-semibold text-gray-700">
                    {time.substring(0, 5)}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Columna de turnos */}
          <div className="flex-1 relative">
            {/* Grid de fondo */}
            {timeSlots.map((time, index) => {
              const isBreak = isBreakTime(time);
              const isOutside = isOutsideWorkHours(time);
              const isBlocked = isBlockedSlot(time);
              const isPast = isPastTime(time);
              const isFree = !isBreak && !isOutside && !isBlocked && !isPast && isSlotFree(time);
              const isOverrideable = !isBreak && !isOutside && !isBlocked && !isPast && canAddOverride(time);
              const isClickable = isFree || isOverrideable;

              return (
                <div
                  key={time}
                  className={`
                    border-b border-gray-300 transition-colors relative
                    ${isPast && !isBreak && !isOutside ? 'bg-gray-100/80' : ''}
                    ${isBlocked && !isPast ? 'bg-red-100/60' : ''}
                    ${isBreak && !isBlocked ? 'bg-gray-300/60' : ''}
                    ${isOutside && !isBreak && !isBlocked ? 'bg-gray-200/80' : ''}
                    ${!isBreak && !isOutside && !isBlocked && !isPast ? 'bg-white hover:bg-gray-50/70' : ''}
                    ${isClickable && onAddAppointment ? 'cursor-pointer group/slot' : ''}
                    ${isPast ? 'opacity-50' : ''}
                  `}
                  style={{ height: `${SLOT_HEIGHT}px` }}
                  onClick={() => isClickable && handleSlotClick(time)}
                >
                  {/* Indicador de receso */}
                  {isBreak && time === horario?.break_start?.substring(0, 5) && (
                    <div className="absolute inset-x-0 top-0 flex items-center justify-center z-5">
                      <span className="text-[10px] font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-b border border-t-0 border-gray-300">
                        ☕ Receso
                      </span>
                    </div>
                  )}
                  {/* Indicador de bloqueo */}
                  {isBlocked && time === blockedHours?.blockedFrom && (
                    <div className="absolute inset-x-0 top-0 flex items-center justify-center z-5">
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded-b border border-t-0 border-red-200">
                        🚫 {blockedHours?.blockedUntil
                          ? `Bloqueado de ${blockedHours.blockedFrom} a ${blockedHours.blockedUntil}`
                          : `Bloqueado desde ${blockedHours?.blockedFrom}`}
                      </span>
                    </div>
                  )}
                  {/* Botón + en slots vacíos (visible en mobile como icono discreto, texto en hover en desktop) */}
                  {isFree && onAddAppointment && (
                    <div className="absolute inset-y-0 right-2 md:inset-0 flex items-center md:justify-center opacity-80 md:opacity-0 group-hover/slot:opacity-100 transition-opacity z-5 pointer-events-none">
                      <span className="md:hidden flex items-center justify-center w-6 h-6 rounded-full bg-blue-50/90 text-blue-600 border border-blue-200 shadow-sm text-lg leading-none">
                        +
                      </span>
                      <span className="hidden md:inline-block text-xs text-gray-400 font-medium bg-white/90 px-2 py-0.5 rounded border border-gray-200 shadow-sm">
                        + Agregar turno {time}
                      </span>
                    </div>
                  )}
                  {/* Botón + sobreturno en slots ocupados (visible en mobile como icono discreto, texto en hover en desktop) */}
                  {isOverrideable && onAddAppointment && (
                    <div className="absolute inset-y-0 right-2 flex items-center justify-end opacity-90 md:opacity-0 group-hover/slot:opacity-100 transition-opacity z-5 pointer-events-none">
                      <span className="md:hidden flex items-center justify-center px-1.5 py-0.5 rounded bg-orange-50/95 border border-orange-200 text-orange-600 text-[10px] font-bold shadow-sm">
                        +ST
                      </span>
                      <span className="hidden md:inline-block text-xs text-orange-600 font-medium bg-orange-50/95 px-2 py-0.5 rounded border border-orange-200 shadow-sm">
                        + Sobreturno {time}
                      </span>
                    </div>
                  )}
                  {/* Línea de media hora */}
                  {time.endsWith(':00') && (
                    <div className="absolute left-0 right-0 border-b border-gray-400" style={{ top: `${SLOT_HEIGHT / 2}px` }} />
                  )}

                  {/* Líneas verticales cada hora */}
                  {time.endsWith(':00') && index > 0 && (
                    <div className="absolute left-0 h-full border-l border-gray-400 opacity-40"></div>
                  )}
                </div>
              );
            })}

            {/* Líneas de hora completa más marcadas */}
            <div className="absolute inset-0 pointer-events-none">
              {timeSlots.filter(time => time.endsWith(':00')).map((time, index) => {
                const minutes = timeToMinutes(time);
                const baseMinutes = START_HOUR * 60;
                const topPosition = ((minutes - baseMinutes) / 30) * SLOT_HEIGHT;
                
                return (
                  <div 
                    key={`hour-${time}`}
                    className="absolute left-0 right-0 border-t border-gray-400" 
                    style={{ top: `${topPosition}px` }}
                  />
                );
              })}
            </div>

            {/* Turnos posicionados absolutamente */}
            <div className="absolute inset-0 pointer-events-none">
              {dayAppointments.map((appointment) => {
                const style = getAppointmentStyle(appointment);
                const colors = getStatusColor(appointment.status);
                const badge = getStatusBadge(appointment.status);
                const { totalColumns, hidden } = getOverlapInfo(appointment);
                const isStacked = totalColumns > 1;
                const slotTime = appointment.appointment_time.substring(0, 5);
                const isExpanded = expandedSlots.has(slotTime);

                // Hide overflow appointments unless slot is expanded
                if (hidden && !isExpanded) return null;

                return (
                  <div
                    key={appointment.id}
                    className={`
                      absolute ${!isStacked ? 'left-1 right-1 mx-1' : ''}
                      ${colors.bg} ${colors.border}
                      border-l-4 rounded-r-lg
                      ${appointment.status === 'cancelled' ? 'opacity-50' : 'shadow-sm hover:shadow-md hover:scale-[1.02]'}
                      transition-all duration-200 ease-out
                      cursor-pointer pointer-events-auto active:scale-[0.98]
                      group overflow-hidden
                      ${appointment.is_override ? 'border-dashed border-r-2 border-t-2 border-b-2 border-orange-300' : ''}
                    `}
                    style={style}
                    onClick={() => onAppointmentClick(appointment)}
                  >
                    <div className="p-2 h-full flex flex-col justify-between">
                      {/* Info principal */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-semibold text-sm ${colors.text} truncate flex items-center gap-1 ${appointment.status === 'cancelled' ? 'line-through' : ''}`}>
                              {appointment.is_override && (
                                <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 flex-shrink-0">
                                  ST
                                </span>
                              )}
                              {appointment.client_display_name.toUpperCase()}
                            </h4>
                            <p className={`text-xs text-gray-600 truncate mt-0.5 flex items-center ${appointment.status === 'cancelled' ? 'line-through' : ''}`}>
                              <span className="font-bold text-gray-800 mr-1.5">{appointment.appointment_time.substring(0, 5)}</span>
                              <span className="truncate">{appointment.service_name}</span>
                              {appointment.location_name && (
                                <span className="text-purple-600 ml-1.5 flex-shrink-0">📍{appointment.location_name}</span>
                              )}
                            </p>
                          </div>

                          {/* Botón Sobreturno */}
                          {maxOverridesPerSlot > 0 && (onQuickAdd || onAddAppointment) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const time = appointment.appointment_time.substring(0, 5);
                                if (onQuickAdd) {
                                  // Abrir QuickAdd inline para sobreturno
                                  const slotMinutes = timeToMinutes(time);
                                  const baseMinutes = START_HOUR * 60;
                                  const topPx = ((slotMinutes - baseMinutes) / 30) * SLOT_HEIGHT;
                                  setQuickAdd({ time, top: topPx });
                                  setQaTime(time); setQaName(''); setQaPhone(''); setQaService(''); setQaServiceId(null);
                                  setQaDuration(30); setQaNotes(''); setQaSaving(false); setQaIndicaciones(''); setQaSendWhatsApp(true);
                                } else if (onAddAppointment) {
                                  const dateStr = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}`;
                                  onAddAppointment(dateStr, time);
                                }
                              }}
                              className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-white shadow-sm hover:shadow transition-all ml-1 opacity-100 md:opacity-0 md:group-hover:opacity-100"
                              title="Agregar sobreturno"
                            >
                              <span className="text-[10px] font-bold">+ST</span>
                            </button>
                          )}
                          {/* Botón WhatsApp */}
                          {appointment.client_phone && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onWhatsAppClick(appointment);
                              }}
                              className="flex-shrink-0 w-7 h-7 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white shadow-sm hover:shadow transition-all ml-1 opacity-70 group-hover:opacity-100"
                              title="Contactar por WhatsApp"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Info secundaria (solo si hay espacio) */}
                      {appointment.duration >= 30 && (
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${badge.class}`}>
                            {badge.label}
                          </span>
                          <span className="text-xs text-gray-500 font-medium">
                            {appointment.duration} min
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Overflow indicators: "+N más" badges for slots with hidden appointments */}
              {Array.from(slotOverflowInfo.entries()).map(([time, info]) => {
                const isExpanded = expandedSlots.has(time);
                if (isExpanded) return null; // Don't show badge when expanded
                const startMinutes = timeToMinutes(time);
                const baseMinutes = START_HOUR * 60;
                const topPosition = ((startMinutes - baseMinutes) / 30) * SLOT_HEIGHT;

                return (
                  <button
                    key={`overflow-${time}`}
                    className="absolute pointer-events-auto z-20 flex flex-col items-center justify-center bg-gradient-to-b from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer border border-orange-400"
                    style={{
                      top: `${topPosition + 4}px`,
                      right: '2px',
                      width: '14%',
                      height: `${Math.max(SLOT_HEIGHT - 8, 44)}px`,
                    }}
                    onClick={() => {
                      setExpandedSlots(prev => {
                        const next = new Set(prev);
                        next.add(time);
                        return next;
                      });
                    }}
                    title={`Ver ${info.hidden} turno(s) más a las ${time}`}
                  >
                    <span className="text-sm font-bold leading-none">+{info.hidden}</span>
                    <span className="text-[9px] leading-none mt-0.5">más</span>
                  </button>
                );
              })}
            </div>

            {/* Expanded slot panels: vertical list of all appointments in an overflowed time */}
            {Array.from(expandedSlots).map(time => {
              const overflow = slotOverflowInfo.get(time);
              if (!overflow) return null;
              // Get ALL appointments at this time slot
              const slotAppts = dayAppointments.filter(a => a.appointment_time.substring(0, 5) === time);
              const startMinutes = timeToMinutes(time);
              const baseMinutes = START_HOUR * 60;
              const topPosition = ((startMinutes - baseMinutes) / 30) * SLOT_HEIGHT;

              return (
                <div
                  key={`expanded-${time}`}
                  className="absolute left-0 right-0 z-40 pointer-events-auto"
                  style={{ top: `${topPosition + SLOT_HEIGHT}px` }}
                >
                  <div className="mx-2 bg-white border-2 border-orange-400 rounded-xl shadow-2xl overflow-hidden"
                    style={{ animation: 'fadeIn 0.2s ease-out' }}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-200">
                      <span className="text-sm font-semibold text-orange-800">
                        {slotAppts.length} turnos a las {time}
                      </span>
                      <button
                        onClick={() => {
                          setExpandedSlots(prev => {
                            const next = new Set(prev);
                            next.delete(time);
                            return next;
                          });
                        }}
                        className="w-6 h-6 rounded-full bg-orange-100 hover:bg-orange-200 flex items-center justify-center text-orange-600 transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                    {/* Scrollable appointment list */}
                    <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                      {slotAppts.map(apt => {
                        const colors = getStatusColor(apt.status);
                        const badge = getStatusBadge(apt.status);
                        return (
                          <div
                            key={apt.id}
                            className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${colors.bg}`}
                            onClick={() => onAppointmentClick(apt)}
                          >
                            <div className={`w-1 h-8 rounded-full ${colors.border.replace('border-l-', 'bg-')}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                {apt.is_override && (
                                  <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-300">ST</span>
                                )}
                                <span className="font-semibold text-sm text-gray-900 truncate">{apt.client_display_name}</span>
                              </div>
                              <span className="text-xs text-gray-500 truncate block">{apt.service_name} · {apt.duration}min</span>
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${badge.class}`}>{badge.label}</span>
                            {apt.client_phone && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onWhatsAppClick(apt); }}
                                className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white"
                              >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Indicador de hora actual */}
            <CurrentTimeIndicator startHour={START_HOUR} slotHeight={SLOT_HEIGHT} />

            {/* QuickAdd Inline Form */}
            {quickAdd && onQuickAdd && (
              <div
                className="absolute left-2 right-2 z-30 pointer-events-auto"
                style={{ top: `${quickAdd.top}px` }}
              >
                <div className={`bg-white border-2 ${!isSlotFree(qaTime) ? 'border-orange-500' : 'border-blue-500'} rounded-xl shadow-2xl p-3 sm:p-4 space-y-2 sm:space-y-3 min-w-[280px]`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {!isSlotFree(qaTime) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300">⚡ ST</span>
                      )}
                      <h4 className={`text-sm font-bold ${!isSlotFree(qaTime) ? 'text-orange-700' : 'text-blue-700'}`}>
                        {!isSlotFree(qaTime) ? 'Sobreturno rápido —' : 'Turno rápido —'}
                      </h4>
                      <input
                        type="time"
                        value={qaTime}
                        onChange={e => setQaTime(e.target.value)}
                        className={`px-2 py-1 border rounded-lg text-sm font-bold focus:ring-2 w-24 ${
                          !isSlotFree(qaTime)
                            ? 'border-orange-300 text-orange-700 focus:ring-orange-500'
                            : 'border-blue-300 text-blue-700 focus:ring-blue-500'
                        }`}
                      />
                    </div>
                    <button onClick={() => setQuickAdd(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
                  </div>

                  {/* Paciente */}
                  <input
                    type="text" placeholder="Nombre del paciente *" autoFocus
                    value={qaName} onChange={e => setQaName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />

                  {/* Servicio: combobox (escribir o elegir) */}
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text" placeholder="Servicio (escribir o elegir)"
                        value={qaService}
                        onChange={e => { setQaService(e.target.value); setQaServiceId(null); }}
                        onBlur={() => {
                          const match = services.find(s => s.name.toLowerCase() === qaService.toLowerCase());
                          if (match) { setQaServiceId(match.id); setQaDuration(match.duration); }
                        }}
                        list="qa-services-list"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <datalist id="qa-services-list">
                        {services.map(s => (
                          <option key={s.id} value={s.name} />
                        ))}
                      </datalist>
                    </div>
                    <input
                      type="number" min={5} step={5} placeholder="Min"
                      value={qaDuration} onChange={e => setQaDuration(parseInt(e.target.value) || 30)}
                      className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Teléfono + Notas */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="tel" placeholder="Teléfono (opcional)"
                      value={qaPhone} onChange={e => setQaPhone(e.target.value)}
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text" placeholder="Notas internas (opcional)"
                      value={qaNotes} onChange={e => setQaNotes(e.target.value)}
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Indicaciones al paciente + toggle WhatsApp */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-green-700 flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                        </svg>
                        Indicaciones al paciente
                      </label>
                      <button
                        type="button"
                        onClick={() => setQaSendWhatsApp(!qaSendWhatsApp)}
                        className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${qaSendWhatsApp ? 'bg-green-500' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${qaSendWhatsApp ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Ej: Traer estudios previos, venir en ayunas..."
                      value={qaIndicaciones}
                      onChange={e => setQaIndicaciones(e.target.value)}
                      className="w-full px-3 py-2 border border-green-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    />
                    <p className="text-[10px] text-green-600">
                      {qaSendWhatsApp ? '✓ Se enviará con la confirmación por WhatsApp' : '✗ No se incluirá en el WhatsApp'}
                    </p>
                  </div>

                  {/* Botones */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleQuickAddSubmit}
                      disabled={qaSaving || !qaName.trim() || !qaService.trim()}
                      className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                        !isSlotFree(qaTime)
                          ? 'bg-orange-500 hover:bg-orange-600'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {qaSaving ? 'Guardando...' : !isSlotFree(qaTime) ? '⚡ Agendar sobreturno' : 'Agendar'}
                    </button>
                    <button
                      onClick={() => setQuickAdd(null)}
                      className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer con leyenda */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-100/70">
        <div className="flex items-center justify-center space-x-4 text-xs text-gray-600 flex-wrap gap-y-1">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded bg-emerald-100 border-l-2 border-emerald-500"></div>
            <span className="font-medium">Confirmado</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded bg-amber-100 border-l-2 border-amber-500"></div>
            <span className="font-medium">Pendiente</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded bg-gray-300"></div>
            <span className="font-medium">Receso</span>
          </div>
          {blockedHours && (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-red-100 border border-red-300"></div>
              <span className="font-medium text-red-600">Bloqueado</span>
            </div>
          )}
          {onAddAppointment && (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 rounded bg-orange-100 border border-dashed border-orange-300"></div>
              <span className="font-medium text-orange-600">Sobreturno</span>
            </div>
          )}
        </div>
      </div>

      {/* Modal para abrir el día (día especial). Los dos modales van al body,
          como los demás del calendario: adentro de la agenda quedaban debajo
          del encabezado de la app (z-[100]), que les tapaba la parte de
          arriba en pantallas bajas. */}
      {showOpenModal && createPortal((() => {
        const rangoMal = !openFrom || !openUntil || openUntil <= openFrom;
        const corteMal = openConCorte && (!openBreakFrom || !openBreakUntil
          || openBreakUntil <= openBreakFrom || openBreakFrom <= openFrom || openBreakUntil >= openUntil);
        const faltaSede = sedes.length > 1 && !openSedeId;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 text-left">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Abrir este día</h3>
                  <p className="text-xs text-gray-500 first-letter:uppercase">{formattedDate}</p>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Desde las:</label>
                    <input
                      type="time"
                      value={openFrom}
                      onChange={(e) => setOpenFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hasta las:</label>
                    <input
                      type="time"
                      value={openUntil}
                      onChange={(e) => setOpenUntil(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                {rangoMal && openFrom && openUntil && (
                  <p className="text-xs text-red-500 -mt-1">El "hasta" debe ser posterior al "desde".</p>
                )}

                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={openConCorte}
                    onChange={(e) => setOpenConCorte(e.target.checked)}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Con corte (por ejemplo, al mediodía)
                </label>
                {openConCorte && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Corte desde:</label>
                      <input
                        type="time"
                        value={openBreakFrom}
                        onChange={(e) => setOpenBreakFrom(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Hasta:</label>
                      <input
                        type="time"
                        value={openBreakUntil}
                        onChange={(e) => setOpenBreakUntil(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                )}
                {corteMal && openBreakFrom && openBreakUntil && (
                  <p className="text-xs text-red-500 -mt-1">El corte tiene que quedar dentro del horario.</p>
                )}

                {sedes.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sede</label>
                    <select
                      value={openSedeId ?? ''}
                      onChange={(e) => setOpenSedeId(e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm bg-white"
                    >
                      <option value="">Elegí la sede…</option>
                      {sedes.map((sd) => (
                        <option key={sd.id} value={sd.id}>{sd.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opcional)</label>
                  <input
                    type="text"
                    value={openReason}
                    onChange={(e) => setOpenReason(e.target.value)}
                    placeholder="Ej: Atiende antes del congreso"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Los pacientes van a poder reservar ese día en este horario.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowOpenModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (!onOpenSpecialDay || rangoMal || corteMal || faltaSede) return;
                    setIsOpening(true);
                    try {
                      const result = await onOpenSpecialDay({
                        start: openFrom,
                        end: openUntil,
                        breakStart: openConCorte ? openBreakFrom : undefined,
                        breakEnd: openConCorte ? openBreakUntil : undefined,
                        reason: openReason.trim() || undefined,
                        locationId: sedes.length > 1 ? openSedeId : (sedes[0]?.id ?? null)
                      });
                      if (result !== false) setShowOpenModal(false);
                    } catch (err) {
                      console.error('Error al abrir el día:', err);
                    } finally {
                      setIsOpening(false);
                    }
                  }}
                  disabled={isOpening || rangoMal || corteMal || faltaSede}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {isOpening ? 'Abriendo…' : 'Abrir día'}
                </button>
              </div>
            </div>
          </div>
        );
      })(), document.body)}

      {/* Modal de bloqueo de horarios */}
      {showBlockModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 text-left">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Bloquear horarios</h3>
                <p className="text-xs text-gray-500">Los pacientes no podrán agendar</p>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desde las:
                  </label>
                  <input
                    type="time"
                    value={blockFromTime}
                    onChange={(e) => setBlockFromTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hasta las:
                  </label>
                  <input
                    type="time"
                    value={blockUntilTime}
                    onChange={(e) => setBlockUntilTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 -mt-1">
                Dejá "hasta" vacío para bloquear el resto del día.
              </p>
              {blockUntilTime && blockFromTime && blockUntilTime <= blockFromTime && (
                <p className="text-xs text-red-500 -mt-1">
                  El "hasta" debe ser posterior al "desde".
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo (opcional)
                </label>
                <input
                  type="text"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Ej: Trámites personales"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowBlockModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (onBlockHours && blockFromTime) {
                    setIsBlocking(true);
                    try {
                      const result = await onBlockHours(blockFromTime, blockUntilTime || undefined, blockReason || undefined);
                      if (result !== false) {
                        setShowBlockModal(false);
                        setBlockUntilTime('');
                      }
                    } catch (err) {
                      console.error('Error al bloquear:', err);
                    } finally {
                      setIsBlocking(false);
                    }
                  }
                }}
                disabled={!blockFromTime || isBlocking || (!!blockUntilTime && blockUntilTime <= blockFromTime)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isBlocking ? 'Bloqueando...' : 'Bloquear'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE: Indicador de hora actual
// =====================================================

const CurrentTimeIndicator: React.FC<{ startHour: number; slotHeight: number }> = ({ 
  startHour, 
  slotHeight 
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const now = currentTime;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const baseMinutes = startHour * 60;
  const topPosition = ((currentMinutes - baseMinutes) / 30) * slotHeight;

  // Solo mostrar si está dentro del rango visible
  if (currentMinutes < baseMinutes || currentMinutes > 22 * 60) {
    return null;
  }

  return (
    <div
      className="absolute left-0 right-0 z-10 pointer-events-none"
      style={{ top: `${topPosition}px` }}
    >
      <div className="flex items-center">
        <div className="w-3 h-3 rounded-full bg-red-600 shadow-lg" />
        <div className="flex-1 h-1 bg-red-500/80" />
      </div>
      <div className="absolute -top-2 right-2 text-xs font-bold text-red-600 bg-white px-1 rounded">
        {now.getHours().toString().padStart(2, '0')}:{now.getMinutes().toString().padStart(2, '0')}
      </div>
    </div>
  );
};

export default DayTimelineView;
export type { QuickAddData, ServiceOption, SpecialDayInfo, OpenSpecialDayData, DiaCerradoInfo };
export { cierreDelDia };